import type {
  ApplovinClickIds,
  ApplovinLanding,
  MarketingTracking,
} from '@openeducation/pp-rn-shared-types';

const AXWRT_COOKIE_NAME = '_axwrt';
const LANDING_STORAGE_KEY_PREFIX = 'applovin-landing:';

const getLocationSearch = () =>
  typeof window !== 'undefined' ? window.location?.search ?? '' : '';

const getLocationHref = () =>
  typeof window !== 'undefined' ? window.location?.href : undefined;

const getDocumentCookie = () =>
  typeof document !== 'undefined' ? document.cookie ?? '' : '';

// AppLovin adds aleid and alart to the checkout URL when the user comes from
// an AppLovin ad. Both are optional.
export function getApplovinClickIds(
  search: string = getLocationSearch()
): ApplovinClickIds {
  const params = new URLSearchParams(search);
  const aleid = params.get('aleid');
  const alart = params.get('alart');
  return {
    ...(aleid ? { aleid } : {}),
    ...(alart ? { alart } : {}),
  };
}

const hasClickId = ({ aleid, alart }: ApplovinClickIds) => !!aleid || !!alart;

// The Axon pixel (the GTM Init tag) sets the _axwrt cookie.
export function getAxwrtCookie(
  cookie: string = getDocumentCookie()
): string | undefined {
  const prefix = `${AXWRT_COOKIE_NAME}=`;
  const value = cookie
    .split(';')
    .map((entry) => entry.trim())
    .find((entry) => entry.startsWith(prefix))
    ?.slice(prefix.length);
  return value || undefined;
}

// The pixel can set the cookie after the checkout loads, so check it again
// until it is there or the time is over.
export function waitForAxwrtCookie({
  intervalMs = 100,
  timeoutMs = 1500,
}: { intervalMs?: number; timeoutMs?: number } = {}): Promise<
  string | undefined
> {
  return new Promise((resolve) => {
    let elapsedMs = 0;
    const check = () => {
      const axwrt = getAxwrtCookie();
      if (axwrt || elapsedMs >= timeoutMs) {
        resolve(axwrt);
        return;
      }
      elapsedMs += intervalMs;
      setTimeout(check, intervalMs);
    };
    check();
  });
}

const getLandingKey = (sfdcToken: string) =>
  `${LANDING_STORAGE_KEY_PREFIX}${sfdcToken}`;

const getUrlLanding = (): ApplovinLanding | undefined => {
  const href = getLocationHref();
  return href ? { ...getApplovinClickIds(), eventSourceUrl: href } : undefined;
};

const readSavedLanding = (sfdcToken: string): ApplovinLanding | undefined => {
  try {
    const saved = sessionStorage.getItem(getLandingKey(sfdcToken));
    const landing = saved ? JSON.parse(saved) : undefined;
    return typeof landing?.eventSourceUrl === 'string' ? landing : undefined;
  } catch {
    return undefined;
  }
};

// The PayPal flow leaves the checkout and comes back to another URL, without
// aleid and alart. Call this when the checkout loads, to keep the landing data
// in sessionStorage for the sfdcToken. It does not replace the saved data with
// a URL that has no click ids (for example the PayPal return URL).
export function saveApplovinLanding(sfdcToken: string) {
  const landing = getUrlLanding();
  if (!landing) {
    return;
  }
  try {
    if (!hasClickId(landing) && readSavedLanding(sfdcToken)) {
      return;
    }
    sessionStorage.setItem(getLandingKey(sfdcToken), JSON.stringify(landing));
  } catch {
    // No sessionStorage (native app, private mode): use the URL only.
  }
}

// Returns the landing data of the URL when it has click ids, or else the data
// saved for the sfdcToken, or else the URL without click ids. Returns undefined
// only when there is no page URL.
export function getApplovinLanding(
  sfdcToken: string
): ApplovinLanding | undefined {
  const urlLanding = getUrlLanding();
  if (urlLanding && hasClickId(urlLanding)) {
    return urlLanding;
  }
  return readSavedLanding(sfdcToken) ?? urlLanding;
}

// Sent with the payment submit on every checkout. The click ids and axwrt are
// optional. Returns undefined only when there is no page URL.
export function getMarketingTracking(
  sfdcToken: string
): MarketingTracking | undefined {
  const landing = getApplovinLanding(sfdcToken);
  if (!landing) {
    return undefined;
  }
  const axwrt = getAxwrtCookie();
  return {
    ...landing,
    ...(axwrt ? { axwrt } : {}),
  };
}
